/**
 * Gestor - Dispositivos WhatsApp
 * Fluxo: Clica "Adicionar" -> Mostra QR Code -> Escaneia -> Auto-detecta e salva
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, useToast, ConfirmDialog } from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

interface WhatsAppDevice {
  id: string;
  nome: string;
  numero: string | null;
  status: "connected" | "disconnected" | "pending";
  isDefault: boolean;
  webhookUrl: string | null;
  createdAt: string;
}

export function GestorDevicesPage() {
  const [devices, setDevices] = useState<WhatsAppDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppDevice | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  // QR Code state
  const [showQR, setShowQR] = useState(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  const [polling, setPolling] = useState(false);
  const [deviceAdded, setDeviceAdded] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const qrDataRef = useRef<{ appkey: string; deviceId: string } | null>(null);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
  });

  const fetchDevices = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/admin/whatsapp/devices`, {
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success) setDevices(json.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const handleGenerateQR = async () => {
    setShowQR(true);
    setQrLoading(true);
    setQrError("");
    setQrImage(null);
    setDeviceAdded(false);

    try {
      const res = await fetch(`${API_BASE}/v1/admin/whatsapp/devices/qrcode`, {
        method: "POST",
        headers: getHeaders(),
      });
      const json = await res.json();

      if (json.success && json.data?.qr) {
        setQrImage(json.data.qr);
        qrDataRef.current = {
          appkey: json.data.appkey,
          deviceId: json.data.deviceId,
        };
        startPolling();
      } else {
        setQrError(json.error?.message || "Erro ao gerar QR Code");
      }
    } catch {
      setQrError("Erro de conexao com o servidor");
    } finally {
      setQrLoading(false);
    }
  };

  const startPolling = () => {
    setPolling(true);
    const poll = async () => {
      if (!qrDataRef.current) return;
      const { appkey, deviceId } = qrDataRef.current;

      try {
        const res = await fetch(
          `${API_BASE}/v1/admin/whatsapp/devices/qrcode-status?appkey=${encodeURIComponent(appkey)}&deviceId=${encodeURIComponent(deviceId)}`,
          { headers: getHeaders() }
        );
        const json = await res.json();

        if (json.success && json.data?.connected) {
          // Conectado! Backend ja atualizou o device
          setDeviceAdded(true);
          setPolling(false);
          fetchDevices();
          setTimeout(() => closeQR(), 2000);
          return;
        }
      } catch {
        // Silently retry
      }

      // Continuar polling a cada 5s (mesmo padrao do PHP: 10s, mas mais responsivo)
      pollRef.current = setTimeout(poll, 5000);
    };

    pollRef.current = setTimeout(poll, 5000);
  };

  const stopPolling = () => {
    setPolling(false);
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    qrDataRef.current = null;
  };

  const closeQR = () => {
    stopPolling();
    setShowQR(false);
    setQrImage(null);
    setQrError("");
    setDeviceAdded(false);
  };

  const handleCheckStatus = async (device: WhatsAppDevice) => {
    setCheckingStatus(device.id);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/whatsapp/devices/${device.id}/status`, {
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success) {
        toast({ variant: "success", title: "Status atualizado" });
        fetchDevices();
      }
    } catch {
      toast({ variant: "error", title: "Erro ao verificar status" });
    } finally {
      setCheckingStatus(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/whatsapp/devices/${deleteTarget.id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success) {
        toast({ variant: "success", title: "Dispositivo removido" });
        fetchDevices();
      }
    } catch {
      toast({ variant: "error", title: "Erro ao remover dispositivo" });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Dispositivos WhatsApp</h1>
        <Button onClick={handleGenerateQR} disabled={showQR}>
          Adicionar
        </Button>
      </div>

      {/* QR Code Panel */}
      {showQR && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Conectar novo dispositivo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4 py-4">
              {qrLoading && (
                <div className="flex flex-col items-center gap-3">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
                  <p className="text-sm text-muted-foreground">Gerando QR Code...</p>
                </div>
              )}

              {qrError && (
                <div className="text-center">
                  <p className="text-sm text-red-500">{qrError}</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={handleGenerateQR}>
                    Tentar novamente
                  </Button>
                </div>
              )}

              {qrImage && !deviceAdded && (
                <>
                  <div className="rounded-xl border border-border bg-white p-4">
                    <img
                      src={qrImage}
                      alt="QR Code WhatsApp"
                      className="h-64 w-64"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">
                      Abra o WhatsApp no celular
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Toque em Menu (ou Configuracoes) &gt; Dispositivos conectados &gt; Conectar dispositivo
                    </p>
                    {polling && (
                      <p className="mt-3 flex items-center justify-center gap-2 text-xs text-primary-600">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500" />
                        Aguardando leitura do QR Code...
                      </p>
                    )}
                  </div>
                </>
              )}

              {deviceAdded && (
                <div className="flex flex-col items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-green-700">Dispositivo conectado com sucesso!</p>
                </div>
              )}

              {!deviceAdded && (
                <Button variant="outline" size="sm" onClick={closeQR}>
                  Cancelar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remover dispositivo"
        description={`Tem certeza que deseja remover o dispositivo "${deleteTarget?.nome}"? Esta acao nao pode ser desfeita.`}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
      />

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Nome</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Numero</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Padrao</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {devices.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      Nenhum dispositivo cadastrado
                    </td>
                  </tr>
                ) : (
                  devices.map((device) => (
                    <tr key={device.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium text-foreground">{device.nome}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {device.numero || "\u2014"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={device.status} />
                      </td>
                      <td className="px-4 py-3">
                        {device.isDefault && (
                          <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                            Padrao
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCheckStatus(device)}
                            disabled={checkingStatus === device.id}
                            loading={checkingStatus === device.id}
                          >
                            {checkingStatus === device.id ? "Verificando" : "Verificar"}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteTarget(device)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: WhatsAppDevice["status"] }) {
  const config = {
    connected: { label: "Conectado", className: "bg-green-100 text-green-700" },
    disconnected: { label: "Desconectado", className: "bg-red-100 text-red-700" },
    pending: { label: "Pendente", className: "bg-yellow-100 text-yellow-700" },
  };

  const { label, className } = config[status] || config.pending;

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}
